import type { ChangeEvent, ReactNode } from 'react';

const controlBase =
  'w-full rounded-md bg-background-100 border border-background-300 px-4 py-3 text-base text-foreground-950 placeholder:text-foreground-600 transition-colors duration-200 focus:border-primary-500';

const labelBase = 'block font-label text-sm uppercase tracking-[0.12em] text-foreground-700 mb-2';

function RequiredMark() {
  return (
    <span className="text-primary-400" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

type FieldProps = {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  autoComplete?: string;
  defaultValue?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  className?: string;
};

export function Field({
  label,
  name,
  type = 'text',
  required,
  placeholder,
  hint,
  autoComplete,
  defaultValue,
  inputMode,
  className = '',
}: FieldProps) {
  return (
    <div className={className}>
      <label htmlFor={name} className={labelBase}>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        inputMode={inputMode}
        className={controlBase}
      />
      {hint ? <p className="mt-2 text-[15px] leading-relaxed text-foreground-600">{hint}</p> : null}
    </div>
  );
}

type SelectFieldProps = {
  label: string;
  name: string;
  options: string[];
  required?: boolean;
  hint?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
};

export function SelectField({
  label,
  name,
  options,
  required,
  hint,
  placeholder = 'Please choose\u2026',
  defaultValue,
  onChange,
  className = '',
}: SelectFieldProps) {
  return (
    <div className={className}>
      <label htmlFor={name} className={labelBase}>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <select
        id={name}
        name={name}
        required={required}
        defaultValue={defaultValue ?? ''}
        onChange={onChange}
        className={`${controlBase} cursor-pointer`}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {hint ? <p className="mt-2 text-sm leading-relaxed text-foreground-600">{hint}</p> : null}
    </div>
  );
}

type TextareaFieldProps = {
  label: string;
  name: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
};

export function TextareaField({
  label,
  name,
  required,
  placeholder,
  hint,
  rows = 5,
  maxLength = 2000,
  className = '',
}: TextareaFieldProps) {
  return (
    <div className={className}>
      <label htmlFor={name} className={labelBase}>
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      <textarea
        id={name}
        name={name}
        required={required}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`${controlBase} resize-y leading-relaxed`}
      ></textarea>
      <p className="mt-2 text-[15px] leading-relaxed text-foreground-600">
        {hint ? `${hint} ` : ''}Maximum {maxLength} characters.
      </p>
    </div>
  );
}

type CheckboxFieldProps = {
  label: ReactNode;
  name: string;
  required?: boolean;
  defaultChecked?: boolean;
  checked?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

export function CheckboxField({
  label,
  name,
  required,
  defaultChecked,
  checked,
  onChange,
  className = '',
}: CheckboxFieldProps) {
  return (
    <label htmlFor={name} className={`flex items-start gap-3 cursor-pointer ${className}`}>
      <input
        id={name}
        name={name}
        type="checkbox"
        required={required}
        checked={checked}
        defaultChecked={checked === undefined ? defaultChecked : undefined}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[oklch(var(--primary-500))]"
      />
      <span className="text-[15px] leading-relaxed text-foreground-800">{label}</span>
    </label>
  );
}

/**
 * Reserved anti-spam field. Hidden with a stylesheet class (not inline styles).
 * Input name is intentionally legitimate-looking so AutoFill ignores it.
 */
export function Honeypot() {
  return (
    <div className="form-honeypot" aria-hidden="true">
      <label htmlFor="website_alt">Leave this field empty</label>
      <input
        id="website_alt"
        name="website_alt"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        readOnly
      />
    </div>
  );
}

type FormStatusProps = {
  status: 'idle' | 'success' | 'error';
  successMessage?: string;
  errorMessage?: string;
  className?: string;
};

export function FormStatus({ status, successMessage, errorMessage, className = '' }: FormStatusProps) {
  if (status === 'idle') return null;
  const isSuccess = status === 'success';
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border px-4 py-3 text-[15px] leading-relaxed ${
        isSuccess
          ? 'border-primary-500/40 bg-[#1B1B1B] text-foreground-900'
          : 'border-red-500/50 bg-red-500/10 text-red-200'
      } ${className}`}
    >
      <span className="flex items-start gap-2">
        <i
          className={`${isSuccess ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} text-lg`}
          aria-hidden="true"
        ></i>
        <span>{isSuccess ? successMessage : errorMessage}</span>
      </span>
    </div>
  );
}